import { Section, SectionAffiliation, PhoneNumber, Employment } from "@/types";
import { DateType } from "react-tailwindcss-datepicker";
import { QuestionType } from "@/components/AssessmentQuestion/types";
import { MaterialNames } from "@/components/material_symbol_names";

export type JobActionType = "publish" | "unpublish" | "hide" | "revertToDraft";

export type User = {
  id: string;
  name: string;
  email: string;
};

export type People = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  primaryFacility: Facility;
  primarySectionAffiliation: SectionAffiliation;
  profilePhoto: string;
  pgaId: string;
  phoneNumbers: PhoneNumber[];
  employments: Employment[];
};

export type Compensation = {
  baseMaximum: number;
  baseMinimum: number;
  totalAnticipated: number;
  currencyUnit: string;
};

export type PointOfContact = {
  name: string;
  email?: string;
  title?: string;
  phone?: string;
};

export type Employer = {
  pgaId?: string;
  name: string;
  email?: string;
  phone?: string;
};

export type Hiree = {
  id: string;
  pgaId?: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type JobPostCategory = {
  name: string;
  area: string;
  type: string;
};

export interface JobPostWrapper {
  jobPost: JobPost;
}

export interface Address {
  id: string;
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
}

export interface Facility {
  id: string;
  name: string;
  mainAddress: Address;
}

export interface Applicant {
  id: string;
  appliedOn: string;
  resume?: string;
  email: string;
  phone: string;
  coverLetter?: string;
  website?: string;
  reviewScoreAverage?: number;
  reviews?: Review[];
  applicant: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    pgaId: string;
    latestProgram?: {
      id: string;
      programType?: {
        id: string;
        name: string;
      };
    };
    primarySection?: {
      id: string;
      name: string;
    };
    profilePhoto?: string;
    displayName: string;
    totalJobApplications: number;
    totalActiveJobApplications: number;
  };
}

export interface Review {
  id: string;
  createdAt: string;
  updatedAt: string | null;
  comments: string | null;
  score: number | null;
  isRecommendation: boolean;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  reviewedBy: {
    id: string;
    name: string;
    email: string;
  };
  jobApplicationId?: string | null;
  jobApplication?: any | null;
}

export interface CRMMessage {
  content: string;
  createdAt: string;
  fromOwner: boolean;
  haveAccess: boolean;
  isPrivate: boolean;
}

export interface CRMPerson {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  pgaId: string;
}

export interface CRMInteraction {
  id: string;
  categories: string[];
  createdAt: string;
  interactionDate: string;
  lastMessage: CRMMessage;
  lastModifiedDate: string;
  messages: CRMMessage[];
  messagesCount: number;
  mode: string;
  owner: {
    name: string;
    email: string;
  } | null;
  participants: CRMPerson[];
  relatedTo: string;
  summary: string;
  showAiSummary?: boolean;
  twilioFlexCall?: {
    transcriptSummaryStatus: string;
  } | null;
}

export interface JobPost {
  applications?: Applicant[];
  reviews?: Review[];
  reviewScoreAverage?: number;
  id: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  closedDate?: Date | string | null;
  facility?: Facility;
  section?: Section;
  currentCompensation: number;
  previousCompensation: number;
  owner: User;
  numberOfApplicants: number;
  priority: string;
  status: string;
  subject: string;
  name: string;
  agreedToTermsOfService: boolean;
  serviceLevel: string;
  compensation: Compensation;
  benefits: string;
  additionalIncome: string;
  pointOfContact?: PointOfContact;
  employer?: Employer;
  essentialResponsibilities: string;
  category: JobPostCategory;
  overview: string;
  pgaOrgUrl: string;
  requiredEducation: string;
  requiredExperienceInYears: number;
  requiredExperienceDescription: string;
  hideCompensationFromJobBoard: boolean;
  applicantGroup: string;
  applyVia: string;
  paySchedule: string;
  positionType: string;
  stage: string;
  optInToCareerConsultantAssistance: boolean;
  otherSections: string[];
  otherStates: string[];
  submissionDeadline: Date | string;
  goLiveTime?: Date | string;
  jobPostPublishTime?: Date | string;
  additionalPgaEducation: string;
  externalJobUrl: string;
  stopCaseCloseNotifications: boolean;
  reviewRequestSent?: Date | string;
  isFavorited?: boolean;
  interactions: CRMInteraction[];
  applicationFormUrl?: string;
  jobApplicantsListUrl?: string;
  applicationForm?: {
    id: string;
    referralSourceOptions: string[];
    questions: ApplicationQuestion[];
  };
  consultantVisitedFacility?: YesNoNotSet;
  isFacilityConversion?: YesNoNotSet;
  isPlacementConversion?: YesNoNotSet;
  isHireePgaPro?: YesNoNotSet | "Not Sure";
  isNetNewPosition?: YesNoNotSet | null;
  hiredCompensationAmount?: number | null;
  hiredCompensationSchedule?: string | null;
  closeComments?: string | null;
  sendEmployerSurvey?: boolean;
  hiree?: Hiree | null;
  views?: number;
}

export enum PostStage {
  DRAFT = "Draft",
  PENDING = "Pending",
  ACTIVE = "Active",
  INACTIVE = "Inactive",
  REMOVED = "Removed",
  UNPOSTED = "Unposted",
  IN_REVIEW = "In Review",
  ASSIST = "Assist"
}

export interface JobActionConfig {
  stage: PostStage;
  successMessage: string;
  errorAction: string;
  headline: string;
  buttonText: string;
  icon: MaterialNames;
  modalMessage: string;
}

export const JOB_ACTION_CONFIGS: Record<JobActionType, JobActionConfig> = {
  publish: {
    stage: PostStage.ACTIVE,
    successMessage: "Job published",
    errorAction: "publishing this job",
    headline: "Publish Job",
    buttonText: "Publish",
    icon: "publish",
    modalMessage:
      "Publishing this job will make it visible on the job board to potential applicants. Once published, qualified candidates will be able to view and apply to this position."
  },
  unpublish: {
    stage: PostStage.REMOVED,
    successMessage: "Job unpublished",
    errorAction: "unpublishing this job",
    headline: "Unpublish Job",
    buttonText: "Unpublish",
    icon: "unpublished",
    modalMessage: "Are you sure you want to unpublish this job from the job board? It will be set to the Removed stage."
  },
  hide: {
    stage: PostStage.UNPOSTED,
    successMessage: "Job hidden",
    errorAction: "hiding this job",
    headline: "Hide Job",
    buttonText: "Hide Job",
    icon: "visibility_off",
    modalMessage: "Are you sure you want to hide this job from the job board? It will be set to the Unposted stage."
  },
  revertToDraft: {
    stage: PostStage.DRAFT,
    successMessage: "Job reverted to draft",
    errorAction: "reverting this job to draft",
    headline: "Revert to Draft",
    buttonText: "Revert to Draft",
    icon: "edit_note",
    modalMessage: "Are you sure you want to revert this job to draft? The job will be set to the Draft stage."
  }
};

export const canRevertToDraft = (
  stage: PostStage | string,
  status: HiringStatus | string,
  serviceLevel: ServiceLevel | string
): boolean => {
  const validStages = [PostStage.PENDING, PostStage.ASSIST, PostStage.ACTIVE, PostStage.INACTIVE];
  const invalidStatuses: (HiringStatus | string)[] = [HiringStatus.FILLED, HiringStatus.INCOMPLETE];
  const execuSearchLevels = [ServiceLevel.EXECUTIVE, ServiceLevel.ADVANCED];

  return (
    validStages.includes(stage as PostStage) &&
    !invalidStatuses.includes(status) &&
    !execuSearchLevels.includes(serviceLevel as ServiceLevel)
  );
};

// Convert filled to be closed_complete for query.
// Convert unfilled to be open or closed_incomplete.
export enum HiringStatus {
  FILLED = "Filled",
  INCOMPLETE = "Incomplete",
  IN_PROGRESS = "In Progress",
  EXPIRED = "Expired"
}

export enum ServiceLevel {
  SELF_SERVE = "Self-Serve",
  ASSISTED = "Assisted",
  ADVANCED = "Advanced",
  EXECUTIVE = "Executive"
}

export const isExecuSearchServiceLevel = (serviceLevel: string | null | undefined): boolean => {
  return serviceLevel === ServiceLevel.ADVANCED || serviceLevel === ServiceLevel.EXECUTIVE;
};

export interface JobsQueryVariables {
  searchTerm?: string;
  stage?: string[];
  status?: string[];
  serviceLevel?: string[];
  sectionId?: string;
  facilityId?: string;
  ownerId?: string;
  lowerDateRange?: DateType;
  upperDateRange?: DateType;
}

export interface JobsQueryOrderByVariables {
  orderBy?: {
    field?: string;
    direction?: string;
  };
}

export interface TabItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

export interface JobApplicationWithReviews extends Applicant {
  id: string;
  appliedOn: string;
  resume?: string;
  email: string;
  phone: string;
  coverLetter?: string;
  website?: string;
  reviewScoreAverage?: number;
  reviews?: Review[];
  applicant: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    pgaId: string;
    latestProgram?: {
      id: string;
      programType?: {
        id: string;
        name: string;
      };
    };
    primarySection?: {
      id: string;
      name: string;
    };
    profilePhoto?: string;
    displayName: string;
    totalJobApplications: number;
    totalActiveJobApplications: number;
  };
}

export interface Option {
  id?: string;
  content: string;
  position?: number;
}

export interface ApplicationQuestion {
  id?: string;
  content: string;
  options: Option[];
  position?: number;
  questionType: QuestionType;
}

export type YesNoNotSet = "Yes" | "No" | "Not Set";

export const yesNoNotSetOptions: { value: string; label: string }[] = [
  { value: "Not Set", label: "Not Set" },
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" }
];

/** Supporting copy for employer close-job step and Outcome-tab edit (single source of truth). */
export const employerOutcomeSupportingText = {
  facilityVisited: "Job post's facility was directly visited by a consultant",
  newPositionAtFacility: "Whether this filled role is net-new at the facility or a backfill",
  facilityConversion: "Facility previously did not have a PGA member employed but now does",
  pgaPlacementConversion: "Position was previously occupied by a non-PGA member and is now filled by a PGA member"
} as const;
